/**
 * Rekening Page
 * Displays all user accounts as cards
 * Task 003a - Remove Allocated & Remaining Balance
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Wallet, CreditCard, PiggyBank, Landmark, Edit2, Trash2, WalletCards, TabletSmartphone, Clock, ChevronDown, ChevronUp, MoreVertical, Undo2, Eraser } from 'lucide-react';
import { useAccounts } from '../contexts/AccountContext';
import { usePlans } from '../contexts/PlanContext';
import { useCategories } from '../contexts/CategoryContext';
import Header from '../components/Header';
import { AccountModal } from '../components/AccountModal';
import { AccountDeleteModal } from '../components/AccountDeleteModal';
import { AccountCloseModal } from '../components/AccountCloseModal';
import { Account, AccountType, ACCOUNT_TYPE_DISPLAYS, CreateAccountData, UpdateAccountData } from '../types/account';

import { PixelHourglass } from '../components/PixelHourglass';
import { useUserPreferences } from '../contexts/UserPreferencesContext';

export const RekeningPage: React.FC = () => {
  const { t } = useTranslation();
  const { accounts, closedAccounts, loading, createAccount, updateAccount, deleteAccount, closeAccount, reopenAccount, refreshClosedAccounts } = useAccounts();
  const { formatMoney } = useUserPreferences();
  const { currentMonthPlan } = usePlans();
  const { refreshCategories } = useCategories();
  const navigateRouter = useNavigate();
  const navigate = (page: string) => {
    const route = page === 'budget' ? '/' : `/${page}`;
    window.location.href = route;
  };

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isCloseModalOpen, setIsCloseModalOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [isClosedSectionExpanded, setIsClosedSectionExpanded] = useState(false);
  const [closedAccountsLoaded, setClosedAccountsLoaded] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  const getAccountIcon = (type: AccountType) => {
    switch (type) {
      case 'checking':
        return <WalletCards className="w-6 h-6" />;
      case 'saving':
        return <PiggyBank className="w-6 h-6" />;
      case 'credit_card':
        return <CreditCard className="w-6 h-6" />;
      case 'loan':
        return <Landmark className="w-6 h-6" />;
      case 'cash':
        return <Wallet className="w-6 h-6" />;
      case 'e_wallet':
        return <TabletSmartphone className="w-6 h-6" />;
      default:
        return <Wallet className="w-6 h-6" />;
    }
  };

  const getBalanceColor = (account: Account): string => {
    const isDebtAccount = ACCOUNT_TYPE_DISPLAYS[account.type].is_debt;
    // Credit Card and Loan accounts should always show in red
    if (isDebtAccount) {
      return 'text-red-600';
    }
    return account.balance >= 0 ? 'text-[#077f5d]' : 'text-red-600';
  };

  // Helper to get translated account type name
  const getAccountTypeName = (type: AccountType): string => {
    const typeKeyMap: Record<AccountType, string> = {
      checking: 'type_checking',
      saving: 'type_saving',
      credit_card: 'type_credit_card',
      paylater: 'type_paylater',
      loan: 'type_loan',
      cash: 'type_cash',
      e_wallet: 'type_ewallet'
    };
    return t(`account_modal.${typeKeyMap[type]}`);
  };

  const handleCreateAccount = async (data: CreateAccountData) => {
    await createAccount(data);
    setIsCreateModalOpen(false);

    // Always refresh categories to update budget page calculations
    // - For CC/Paylater/Loan: system group is auto-created
    // - For all accounts: pool changes, affecting "to assign"
    await refreshCategories();
  };

  const handleEditAccount = (account: Account) => {
    setSelectedAccount(account);
    setIsEditModalOpen(true);
  };

  const handleUpdateAccount = async (data: UpdateAccountData) => {
    if (selectedAccount) {
      await updateAccount(selectedAccount.id, data);
      setIsEditModalOpen(false);
      setSelectedAccount(null);

      // Always refresh categories to update budget page calculations
      // Any account change affects pool, affecting "to assign"
      await refreshCategories();
    }
  };

  const handleCloseClick = (account: Account) => {
    setSelectedAccount(account);
    setIsCloseModalOpen(true);
  };

  const handleDeleteClick = (account: Account) => {
    setSelectedAccount(account);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmClose = async () => {
    if (selectedAccount && currentMonthPlan) {
      try {
        await closeAccount(selectedAccount.id, currentMonthPlan.id);
        setIsCloseModalOpen(false);
        setSelectedAccount(null);

        // Always refresh categories to update budget page calculations
        // - For CC/Paylater/Loan: linked_account_id is removed, orphaning system group
        // - For all accounts: pool changes, affecting "to assign"
        await refreshCategories();
      } catch (error) {
        console.error('Close failed:', error);
      }
    }
  };

  const handleConfirmDelete = async () => {
    if (selectedAccount) {
      try {
        await deleteAccount(selectedAccount.id);
        setIsDeleteModalOpen(false);
        setSelectedAccount(null);
        setOpenMenuId(null);

        // Always refresh categories to update budget page calculations
        // - For CC/Paylater/Loan: orphans the linked group
        // - For all accounts: pool changes, affecting "to assign"
        await refreshCategories();

        // Refresh closed accounts list if we deleted a closed account
        if (closedAccountsLoaded) {
          await refreshClosedAccounts();
        }
      } catch (error) {
        console.error('Delete failed:', error);
      }
    }
  };

  const handleReopenAccount = async (account: Account) => {
    try {
      await reopenAccount(account.id);
      setOpenMenuId(null);

      // Always refresh categories to update budget page calculations
      // - For CC/Paylater/Loan: re-links the system group
      // - For all accounts: pool changes, affecting "to assign"
      await refreshCategories();

      // Refresh closed accounts list
      await refreshClosedAccounts();
    } catch (error) {
      console.error('Reopen failed:', error);
    }
  };

  const handleToggleClosedSection = async () => {
    if (!isClosedSectionExpanded && !closedAccountsLoaded) {
      await refreshClosedAccounts();
      setClosedAccountsLoaded(true);
    }
    setIsClosedSectionExpanded(!isClosedSectionExpanded);
  };

  const toggleMenu = (accountId: string) => {
    setOpenMenuId(openMenuId === accountId ? null : accountId);
  };

  const handleViewTransactions = (account: Account) => {
    navigateRouter('/history', {
      state: {
        accountId: account.id
        // Don't pass dateFrom/dateTo to show all transactions for this account
      }
    });
  };

  return (
    <div
      className="min-h-screen bg-[#ffffeb] pb-24 lg:pb-8"
      style={{
        backgroundImage: `
          repeating-linear-gradient(0deg, transparent, transparent 7px, rgba(7, 127, 93, 0.03) 7px, rgba(7, 127, 93, 0.03) 8px),
          repeating-linear-gradient(90deg, transparent, transparent 7px, rgba(7, 127, 93, 0.03) 7px, rgba(7, 127, 93, 0.03) 8px)
        `
      }}
    >
      <Header
        leftContent={
          <>
            <p
              className="text-left text-base font-bold uppercase hover:opacity-80 inline-block"
              style={{
                fontFamily: "'Press Start 2P', monospace",
                transition: 'none',
                fontSize: '14px',
              }}
            >
              {t('rekening.title')}
            </p>
          </>
        }
        rightContent={
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="flex items-center p-2 bg-[#3abf8c] text-white border-2 border-[#077f5d] font-bold uppercase"
            style={{
              fontFamily: "'Press Start 2P', monospace",
              fontSize: '8px',
              boxShadow: '4px 4px 0 #077f5d',
              transition: 'none'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '2px 2px 0 #077f5d';
              e.currentTarget.style.transform = 'translate(-2px, -2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = '4px 4px 0 #077f5d';
              e.currentTarget.style.transform = 'translate(0, 0)';
            }}
          >
            <Plus className="pr-2" size={24} />
            {t('rekening.add_account')}
          </button>
        }
      />

      <div className="px-4 py-6 max-w-7xl mx-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <PixelHourglass size="medium" speed="fast" color="keppel" data-id="budget-status-loader" />
          </div>
        ) : (
          <>
            {/* Active Accounts Section */}
            {accounts.length === 0 ? (
              /* Empty State for Active Accounts */
              <div
                className="bg-white border-4 border-[#077f5d] p-12"
                style={{ boxShadow: '6px 6px 0 #077f5d' }}
              >
                <div className="max-w-md mx-auto text-center">
                  <h3
                    className="text-lg text-[#077f5d] mb-4 uppercase"
                    style={{ fontFamily: "'Press Start 2P', monospace", lineHeight: '1.4' }}
                  >
                    {t('rekening.no_accounts_title')}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {t('rekening.no_accounts_description')}
                  </p>
                  <button
                    onClick={() => setIsCreateModalOpen(true)}
                    className="w-full bg-[#3cbe8c] text-white px-6 py-3 border-2 border-[#077f5d] font-bold uppercase"
                    style={{
                      fontFamily: "'Press Start 2P', monospace",
                      fontSize: '10px',
                      boxShadow: '6px 6px 0 #077f5d'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = '4px 4px 0 #077f5d';
                      e.currentTarget.style.transform = 'translate(-2px, -2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = '6px 6px 0 #077f5d';
                      e.currentTarget.style.transform = 'translate(0, 0)';
                    }}
                  >
                    <Plus
                      className="inline mr-2 mb-1 text-white"
                      size={14}
                      strokeWidth={3}
                    />
                    {t('rekening.add_account')}
                  </button>
                </div>
              </div>
            ) : (
              /* Account Cards Grid */
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {accounts.map((account) => (
                  <div
                    key={account.id}
                    className="bg-white border-2 border-[#077f5d] overflow-hidden"
                    style={{ boxShadow: '4px 4px 0 #077f5d' }}
                  >
                    {/* Account Header - Colored Background */}
                    <div className="bg-[#a9edba] border-b-2 border-[#077f5d] p-4">
                      {/* Account Type Icon and Name */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div>
                            <h3 className="font-bold text-[#1a1a1a] text-lg">{account.name}</h3>
                            <p className="text-xs font-mono text-[#077f5d]">
                              {getAccountTypeName(account.type)}
                            </p>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-1">
                          <button
                            onClick={() => handleViewTransactions(account)}
                            className="p-1 text-[#077f5d] hover:bg-white border-2 border-transparent hover:border-[#077f5d]"
                            style={{ transition: 'none' }}
                            title={t('rekening.view_transactions')}
                          >
                            <Clock className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleEditAccount(account)}
                            className="p-1 text-[#077f5d] hover:bg-white border-2 border-transparent hover:border-[#077f5d]"
                            style={{ transition: 'none' }}
                            title={t('rekening.edit')}
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleCloseClick(account)}
                            className="p-1 text-[#077f5d] hover:bg-white border-2 border-transparent hover:border-[#077f5d]"
                            style={{ transition: 'none' }}
                            title={t('rekening.close_account')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {/* Billing Date for CC/Paylater */}
                      {(account.type === 'credit_card' || account.type === 'paylater') && account.billing_date && (
                        <div>
                          <p className="text-xs font-mono text-[#1a1a1a]">
                            {t('rekening.billing_date')} <span className="font-bold text-[#077f5d]">{account.billing_date}</span>
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Balance Information - White Background */}
                    <div className="p-4">
                      <p className={`text-xl font-bold ${getBalanceColor(account)}`}>
                        {formatMoney(account.balance)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Closed Accounts Section */}
            <div className="mt-8">
              <button
                onClick={handleToggleClosedSection}
                className="w-full flex items-center justify-between p-4 bg-white border-2 border-[#077f5d]"
                style={{ boxShadow: '4px 4px 0 #077f5d' }}
              >
                <h3
                  className="text-[#077f5d] uppercase"
                  style={{ fontFamily: "'Press Start 2P', monospace", fontSize: '12px', lineHeight: '1.4' }}
                >
                  {t('rekening.closed_accounts')}
                </h3>
                {isClosedSectionExpanded ? (
                  <ChevronUp className="w-5 h-5 text-[#077f5d]" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-[#077f5d]" />
                )}
              </button>

              {isClosedSectionExpanded && (
                <div className="mt-4 space-y-4">
                  {!closedAccountsLoaded ? (
                    <div className="flex items-center justify-center py-8">
                      <div
                        className="w-12 h-12 border-4 border-[#077f5d] border-t-transparent"
                        style={{ animation: 'spin 1s linear infinite', borderRadius: '0' }}
                      ></div>
                    </div>
                  ) : closedAccounts.length === 0 ? (
                    <div className="text-center py-8 font-mono text-sm text-[#1a1a1a]">
                      {t('rekening.no_closed_accounts')}
                    </div>
                  ) : (
                    closedAccounts.map((account) => (
                      <div
                        key={account.id}
                        className="bg-[#f5f5f5] border-2 border-[#9ca3af] pt-4 pb-1 pr-6 pl-6 relative"
                        style={{ boxShadow: '3px 3px 0 #9ca3af' }}
                      >
                        {/* Account Type Icon and Name */}
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div
                              className="w-12 h-12 bg-[#d1d5db] border-2 border-[#9ca3af] flex items-center justify-center text-[#6b7280]"
                              style={{ boxShadow: '2px 2px 0 #9ca3af' }}
                            >
                              {getAccountIcon(account.type)}
                            </div>
                            <div>
                              <h3 className="font-bold text-[#6b7280] text-sm">{account.name}</h3>
                              <p className="text-xs font-mono text-[#9ca3af]">
                                {getAccountTypeName(account.type)}
                              </p>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex gap-1">
                            <button
                              onClick={() => handleViewTransactions(account)}
                              className="p-1 text-[#6b7280] hover:bg-[#d1d5db] border-2 border-transparent hover:border-[#9ca3af]"
                              style={{ transition: 'none' }}
                              title={t('rekening.view_transactions')}
                            >
                              <Clock className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => toggleMenu(account.id)}
                              className="p-1 text-[#6b7280] hover:bg-[#d1d5db] border-2 border-transparent hover:border-[#9ca3af]"
                              style={{ transition: 'none' }}
                              title={t('rekening.edit')}
                            >
                              <MoreVertical className="w-4 h-4" />
                            </button>

                            {openMenuId === account.id && (
                              <>
                                {/* Backdrop to close menu */}
                                <div
                                  className="fixed inset-0 z-30"
                                  onClick={() => setOpenMenuId(null)}
                                />
                                {/* Menu Dropdown */}
                                <div
                                  className="absolute right-0 top-12 z-40 bg-white border-2 border-[#077f5d] py-2"
                                  style={{ boxShadow: '4px 4px 0 #077f5d' }}
                                >
                                  <button
                                    onClick={() => {
                                      handleReopenAccount(account);
                                    }}
                                    className="w-full px-4 py-2 text-left text-sm font-mono text-[#077f5d] hover:bg-[#dffbd0] flex items-center gap-2"
                                    style={{ transition: 'none' }}
                                  >
                                    <Undo2 className="w-4 h-4" />
                                    {t('rekening.reopen')}
                                  </button>
                                  <button
                                    onClick={() => {
                                      setOpenMenuId(null);
                                      handleDeleteClick(account);
                                    }}
                                    className="w-full px-4 py-2 text-left text-sm font-mono text-red-600 hover:bg-red-50 flex items-center gap-2"
                                    style={{ transition: 'none' }}
                                  >
                                    <Eraser className="w-4 h-4" />
                                    {t('rekening.delete')}
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Create Account Modal */}
      <AccountModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateAccount as any}
        mode="create"
      />

      {/* Edit Account Modal */}
      {selectedAccount && (
        <AccountModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedAccount(null);
          }}
          onSubmit={handleUpdateAccount}
          mode="edit"
          initialData={{
            name: selectedAccount.name,
            type: selectedAccount.type,
            balance: selectedAccount.balance,
            billing_date: selectedAccount.billing_date,
          }}
        />
      )}

      {/* Close Account Modal */}
      {selectedAccount && (
        <AccountCloseModal
          isOpen={isCloseModalOpen}
          onClose={() => {
            setIsCloseModalOpen(false);
            setSelectedAccount(null);
          }}
          onConfirm={handleConfirmClose}
          accountName={selectedAccount.name}
          currentBalance={selectedAccount.balance}
        />
      )}

      {/* Delete Confirmation Modal */}
      {selectedAccount && (
        <AccountDeleteModal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setSelectedAccount(null);
          }}
          onConfirm={handleConfirmDelete}
          accountName={selectedAccount.name}
          accountId={selectedAccount.id}
        />
      )}
    </div>
  );
};
